import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageShell from "@/components/PageShell";
import {
  Plug, XCircle, Warning, UserPlus, Briefcase, ArrowRight,
  PaperPlaneTilt, ArrowsLeftRight, Lightning,
} from "@phosphor-icons/react";
import { ClientSummary, GrowthChart, ClientPipeline } from "@/components/DashboardWidgets";

// Lightweight count-up
function useCountUp(target = 0, duration = 600) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (target === null || target === undefined) return;
    let raf, start;
    const from = 0, to = Number(target) || 0;
    const tick = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      setV(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return v;
}

function StatCard({ icon: Icon, label, value, hint, tone = "slate", to, testid }) {
  const accent = {
    emerald: "bg-emerald-500",
    red:     "bg-red-500",
    amber:   "bg-amber-500",
    indigo:  "bg-indigo-500",
    slate:   "bg-slate-300",
  }[tone] || "bg-slate-300";
  const animated = useCountUp(value);
  const inner = (
    <div className="relative bg-white border border-slate-200 p-5 transition-colors hover:border-slate-300" data-testid={testid}>
      <div className={`absolute top-0 left-0 right-0 h-[2px] ${accent}`} />
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{label}</div>
        {Icon && <Icon size={14} weight="regular" className="text-slate-400" />}
      </div>
      <div className="mt-3 text-2xl font-semibold font-mono tracking-tight text-slate-900 tabular-nums">{animated}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1 truncate">{hint}</div>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

const LEAD_STAGES = [
  { key: "assigned", label: "Asignado" },
  { key: "contacted", label: "Contactado" },
  { key: "qualified", label: "Calificado" },
  { key: "activating", label: "Activando" },
  { key: "converted", label: "Convertido" },
];

function LeadPipeline({ leads }) {
  const max = Math.max(1, ...LEAD_STAGES.map(s => leads[s.key] || 0));
  return (
    <div className="bg-white border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Pipeline de leads</div>
          <div className="text-sm text-slate-700 mt-0.5">Estado de los leads asignados</div>
        </div>
        <Link to="/support/leads" className="text-xs font-medium text-slate-600 hover:text-slate-900 flex items-center gap-1">
          Ver todos <ArrowRight size={12} />
        </Link>
      </div>
      <div className="space-y-2.5">
        {LEAD_STAGES.map((s, idx) => {
          const count = leads[s.key] || 0;
          const pct = (count / max) * 100;
          return (
            <div key={s.key}>
              <div className="flex items-center justify-between text-xs mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-400 tabular-nums w-4 text-right">{idx + 1}</span>
                  <span className="font-medium text-slate-700">{s.label}</span>
                </div>
                <span className="font-mono font-semibold text-slate-900 tabular-nums">{count}</span>
              </div>
              <div className="h-[6px] bg-slate-100 overflow-hidden">
                <div className="h-full bg-slate-900 transition-all duration-700" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SectionHeader({ eyebrow, title, hint }) {
  return (
    <div className="mb-5 border-b border-slate-200 pb-3">
      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">{eyebrow}</div>
      <h2 className="text-base font-semibold tracking-tight text-slate-900 mt-1.5">{title}</h2>
      {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );
}


export default function SupportDashboard() {
  const [k, setK] = useState(null);
  useEffect(() => {
    const load = () => api.get("/support/kpis").then(r => setK(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  const leads = k?.leads_by_status || {};
  const orders = k?.orders_by_status || {};

  return (
    <PageShell
      section="Support"
      page="Dashboard"
      context="Operaciones"
      testid="support-dashboard-toolbar"
    >
      <div className="space-y-10">
        {/* ═══ SECCIÓN 1 — POSICIÓN GENERAL ═══ */}
        <section data-testid="section-overview">
          <SectionHeader
            eyebrow="Posición General"
            title="Resumen de operaciones"
            hint="Órdenes pendientes y estado del portafolio en vivo"
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard testid="sk-pending-orders" icon={PaperPlaneTilt} label="Nuevas órdenes" value={k?.pending_orders}
              hint="pendientes de aceptar" tone="amber" to="/support/orders" />
            <StatCard testid="sk-active-leads" icon={Briefcase} label="Leads en pipeline" value={k?.active_leads}
              hint="trabajándose ahora" tone="indigo" to="/support/leads" />
            <StatCard testid="sk-active" icon={Plug} label="Clientes activos" value={k?.active_clients}
              hint={`de ${k?.total_clients ?? 0} totales`} tone="emerald" to="/support/clients/active" />
            <StatCard testid="sk-disconnected" icon={XCircle} label="Desconectados" value={k?.disconnected_clients}
              hint="por falta de pago" tone="red" to="/support/clients/disconnected" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="sk-critical" icon={Warning} label="Mora crítica" value={k?.critical_clients}
              hint="requieren contacto" tone="red" />
            <StatCard testid="sk-transferred" icon={ArrowsLeftRight} label="Trasladados" value={k?.transferred_clients}
              hint="contratos cerrados" tone="slate" to="/support/clients/transferred" />
            <StatCard testid="sk-new-leads" icon={UserPlus} label="Leads creados hoy" value={k?.new_leads_today}
              tone="indigo" />
            <StatCard testid="sk-lost-leads" icon={XCircle} label="Leads perdidos" value={k?.lost_leads}
              hint="histórico" tone="slate" to="/support/leads/lost" />
          </div>
        </section>

        {/* ═══ SECCIÓN 2 — PIPELINE Y ÓRDENES ═══ */}
        <section data-testid="section-pipeline">
          <SectionHeader
            eyebrow="Operación"
            title="Pipeline de leads y órdenes"
            hint="Distribución actual del trabajo en Support"
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <LeadPipeline leads={leads} />

            <div className="bg-white border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Órdenes</div>
                  <div className="text-sm text-slate-700 mt-0.5">Distribución por estado</div>
                </div>
                <Link to="/support/orders" className="text-xs font-medium text-slate-600 hover:text-slate-900 flex items-center gap-1">
                  Gestionar <ArrowRight size={12} />
                </Link>
              </div>
              {Object.keys(orders).length === 0 ? (
                <div className="text-sm text-slate-500 py-8 text-center">Sin órdenes aún.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {[
                    { key: "pending", label: "Pendientes", color: "bg-amber-500" },
                    { key: "approved", label: "Aprobadas → Lead", color: "bg-emerald-500" },
                    { key: "rejected", label: "Rechazadas", color: "bg-slate-400" },
                  ].map(s => (
                    <div key={s.key} className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <span className={`w-1.5 h-1.5 ${s.color}`} />
                        <span className="text-sm font-medium text-slate-700">{s.label}</span>
                      </div>
                      <span className="font-mono font-semibold text-slate-900 tabular-nums">{orders[s.key] || 0}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ═══ SECCIÓN 3 — PORTAFOLIO DE CLIENTES ═══ */}
        <section data-testid="section-portfolio">
          <SectionHeader
            eyebrow="Portafolio"
            title="Distribución y crecimiento de clientes"
            hint="Compartido con el equipo financiero"
          />
          <div className="space-y-4">
            <ClientSummary />
            <GrowthChart />
            <ClientPipeline />
          </div>
        </section>
      </div>
    </PageShell>
  );
}
